import { BaseElement } from 'base';
import { bus } from 'bus';

class SegmentedControl extends BaseElement {
  #action = null;
  #committed = null;
  #epoch = 0;
  #pending = null;
  #topic = '';

  onConnect() {
    this.#pending = null;
    this.removeAttribute('aria-busy');
    this.#topic = this.dataset.topic || '';
    this.#committed = this.#ensureChecked();

    this.listen(this, 'change', this.#onChange);
    this.listen(this, 'keydown', this.#onKeydown);

    if (!this.#topic) return;

    let replayed = false;
    this.subscribe(this.#topic, state => {
      replayed = true;
      this.#onTopic(state);
    });
    if (!replayed) this.#publish();
  }

  run(action) {
    this.#action = action;
    return this;
  }

  get value() {
    return this.#committed?.value ?? '';
  }

  get #inputs() {
    return this.qa(
      ':scope > .segmented-control__item > .segmented-control__input'
    );
  }

  #ensureChecked() {
    const inputs = this.#inputs;
    const current =
      inputs.find(input => input.checked) ??
      inputs.find(input => !input.disabled) ??
      inputs[0] ??
      null;
    if (current) current.checked = true;
    return current;
  }

  #commit(input) {
    this.#committed = input;
    this.#epoch += 1;
  }

  #onChange(event) {
    const input = event.target;
    if (!input.checked || !this.#inputs.includes(input)) return;

    if (this.getAttribute('aria-busy') === 'true') {
      if (this.#committed) {
        this.#committed.checked = true;
        this.#committed.focus();
      }
      return;
    }

    if (input === this.#committed) return;

    const previous = this.#committed;
    this.#commit(input);
    const epoch = this.#epoch;
    this.#publish();
    this.#runAction(input, previous, epoch);
  }

  async #runAction(input, previous, epoch) {
    if (!this.#action) return;

    const change = {
      id: this.id,
      value: input.value,
      index: this.#inputs.indexOf(input),
      previous: previous?.value ?? ''
    };

    let pending = null;
    try {
      pending = this.#action(change);
      if (!pending || typeof pending.then !== 'function') return;

      this.#pending = pending;
      this.setAttribute('aria-busy', 'true');
      await pending;
    } catch (error) {
      console.error('[segmented-control] action failed:', error);
      if (!this.isConnected || this.#epoch !== epoch) return;
      this.#rollback(previous);
    } finally {
      if (this.#pending === pending) {
        this.#pending = null;
        this.removeAttribute('aria-busy');
      }
    }
  }

  #rollback(previous) {
    if (!previous) return;

    const hadFocus = this.contains(this.getRootNode().activeElement);
    previous.checked = true;
    this.#commit(previous);
    this.#publish();
    if (hadFocus) previous.focus();
  }

  #publish() {
    if (!this.#topic || !this.#committed) return;

    bus.publish(this.#topic, {
      id: this.id,
      value: this.#committed.value,
      index: this.#inputs.indexOf(this.#committed)
    });
  }

  #onTopic(state) {
    const value = state?.value;
    if (typeof value !== 'string') {
      console.error(
        `[segmented-control] "${this.#topic}" expects { value }, received:`,
        state
      );
      return;
    }

    const input = this.#inputs.find(candidate => candidate.value === value);
    if (!input) {
      console.error(
        `[segmented-control] "${this.#topic}" has no segment "${value}" in #${this.id}`
      );
      return;
    }

    if (input === this.#committed) return;

    input.checked = true;
    this.#commit(input);
  }

  #onKeydown(event) {
    if (event.key !== 'Home' && event.key !== 'End') return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
      return;

    const inputs = this.#inputs;
    if (!inputs.includes(event.target)) return;

    const enabled = inputs.filter(input => !input.disabled);
    const target = event.key === 'Home' ? enabled[0] : enabled.at(-1);
    if (!target) return;

    event.preventDefault();
    if (target === event.target && target.checked) return;

    target.focus();
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('segmented-control'))
  customElements.define('segmented-control', SegmentedControl);
